import { timestamp } from '../utils';
import Nodes from './nodes';
import Observer from './observer';
import Socket from './socket';
import Ticker from './ticker';
import { RawCustomEvent } from '../messages';
import { deviceMemory, jsHeapSizeLimit } from '../modules/performance';
const INTERNAL_ERR_EVENT_NAME = "$$ASAYERRROR$$";
export default class App {
    constructor(projectID, sessionID, opts) {
        this.socketFailedAttempts = 0;
        this.isActive = false;
        this.projectID = projectID;
        this.options = Object.assign({
            revID: '',
            node_id: '__asayer_id',
            session_sid: '__asayer_sid',
            local_uuid: '__asayer_uuid',
            ws_url: 'wss://ws.asayer.io/',
            obscureTextEmails: true,
            obscureTextNumbers: false,
        }, opts);
        if (sessionID != null) {
            sessionStorage.setItem(this.options.session_sid, sessionID);
        }
        this.revID = this.options.revID;
        this.nodes = new Nodes(this.options.node_id);
        this.observer = new Observer(this, this.options);
        this.socket = new Socket((msg) => {
            this.socketFailedAttempts = 0;
            const { sessionID, userUUID } = JSON.parse(String.fromCharCode.apply(null, msg));
            if (typeof sessionID !== 'string' || typeof userUUID !== 'string') {
                this.stop();
                return;
            }
            sessionStorage.setItem(this.options.session_sid, sessionID);
            localStorage.setItem(this.options.local_uuid, userUUID);
            if (typeof this.options.onStart === 'function') {
                this.options.onStart({ sessionID, userUUID });
            }
        }, () => {
            if (sessionStorage.getItem(this.options.session_sid) &&
                this.socketFailedAttempts < 3) {
                this.socketFailedAttempts++;
                this.stop();
                if (!document.hidden) {
                    // FIXME
                    setTimeout(() => this.start(), 1000);
                }
            }
            else {
                this.socketFailedAttempts = 0;
                this.stop();
            }
        });
        this.ticker = new Ticker(this);
        this.ticker.attach(() => this.socket.commit());
        this.startCallbacks = [];
        this.stopCallbacks = [];
    }
    send(data, urgent = false) {
        this.socket.push(data);
        if (urgent) {
            this.socket.commit();
        }
    }
    safe(fn) {
        const app = this;
        return function (...args) {
            try {
                fn.apply(this, args);
            }
            catch (e) {
                app.send(RawCustomEvent(INTERNAL_ERR_EVENT_NAME, JSON.stringify({ name: e.name, message: e.message, stack: e.stack })));
            }
        };
    }
    attachStartCallback(cb) {
        this.startCallbacks.push(cb);
    }
    attachStopCallback(cb) {
        this.stopCallbacks.push(cb);
    }
    attachEventListener(target, type, listener, useSafe = true, useCapture = true) {
        if (useSafe) {
            listener = this.safe(listener);
        }
        this.attachStartCallback(() => target.addEventListener(type, listener, useCapture));
        this.attachStopCallback(() => target.removeEventListener(type, listener, useCapture));
    }
    sessionID() {
        const sid = sessionStorage.getItem(this.options.session_sid);
        if (sid !== null) {
            return sid;
        }
    }
    active() {
        return this.isActive;
    }
    start() {
        if (!this.isActive) {
            this.isActive = true;
            try {
                this.socket.connect(this.options.ws_url +
                    btoa(JSON.stringify({
                        sessionID: sessionStorage.getItem(this.options.session_sid),
                        userUUID: localStorage.getItem(this.options.local_uuid),
                        projectID: this.projectID,
                        revID: this.revID,
                        timestamp: timestamp(),
                        trackerVersion: '5.5.13',
                        deviceMemory,
                        jsHeapSizeLimit,
                    })));
                this.observer.observe();
                this.startCallbacks.forEach((cb) => cb());
                this.ticker.start();
            }
            catch (e) {
                this.stop();
            }
        }
    }
    stop() {
        if (this.isActive) {
            try {
                this.socket.close();
                this.observer.disconnect();
                this.nodes.clear();
                this.ticker.stop();
                this.stopCallbacks.forEach((cb) => cb());
            }
            finally {
                this.isActive = false;
            }
        }
    }
}
